import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdFavorite, MdPoll, MdPeople, MdPerson, MdDelete, MdAccountBalance, MdLocationOn, MdSort, MdLabel } from 'react-icons/md';
import { toast } from 'sonner';
import FavoriteTagsTab from './favorites/FavoriteTagsTab';

const POLL_SORTS = [
  { key: 'recent', label: 'Recently Added' },
  { key: 'alpha', label: 'A - Z' },
];

const REP_SORTS = [
  { key: 'recent', label: 'Recently Added' },
  { key: 'match_desc', label: 'Highest Match' },
  { key: 'match_asc', label: 'Lowest Match' },
  { key: 'alpha', label: 'A - Z' },
];

function sortItems(items, sortKey, type) {
  const sorted = [...items];
  switch (sortKey) {
    case 'recent':
      return sorted.sort((a, b) => (b.added_at || 0) - (a.added_at || 0));
    case 'alpha':
      return sorted.sort((a, b) => {
        const aName = type === 'poll' ? (a.question || '') : (a.name || '');
        const bName = type === 'poll' ? (b.question || '') : (b.name || '');
        return aName.localeCompare(bName);
      });
    case 'match_desc':
      return sorted.sort((a, b) => (b.match_percentage ?? 0) - (a.match_percentage ?? 0));
    case 'match_asc':
      return sorted.sort((a, b) => (a.match_percentage ?? 0) - (b.match_percentage ?? 0));
    default:
      return sorted;
  }
}

export default function FavoritesPage() {
  const [activeTab, setActiveTab] = useState('polls');
  const [polls, setPolls] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollSort, setPollSort] = useState('recent');
  const [repSort, setRepSort] = useState('recent');

  useEffect(() => { loadFavorites(); }, []);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/favorites`);
      setPolls(res.data.polls || []);
      setReps(res.data.representatives || []);
    } catch (err) {
      console.error('Failed to load favorites:', err);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (itemType, itemId, label) => {
    try {
      await axios.delete(`${API_URL}/api/favorites`, { data: { item_type: itemType, item_id: itemId } });
      if (itemType === 'poll') {
        setPolls(prev => prev.filter(p => p.item_id !== itemId));
      } else {
        setReps(prev => prev.filter(r => r.item_id !== itemId));
      }
      toast.success(`Removed "${label}" from favorites`);
    } catch (err) {
      toast.error('Failed to remove favorite');
    }
  };

  const sortedPolls = useMemo(() => sortItems(polls, pollSort, 'poll'), [polls, pollSort]);
  const sortedReps = useMemo(() => sortItems(reps, repSort, 'rep'), [reps, repSort]);

  const tabs = [
    { key: 'polls', label: 'Polls', icon: MdPoll, count: polls.length },
    { key: 'representatives', label: 'Representatives', icon: MdPeople, count: reps.length },
    { key: 'tags', label: 'Tags', icon: MdLabel, count: null },
  ];

  const currentSortOptions = activeTab === 'polls' ? POLL_SORTS : REP_SORTS;
  const currentSort = activeTab === 'polls' ? pollSort : repSort;
  const setCurrentSort = activeTab === 'polls' ? setPollSort : setRepSort;

  return (
    <div className="max-w-4xl mx-auto pt-6 px-4" data-testid="favorites-page">
      <div className="flex items-center gap-3 mb-6">
        <MdFavorite className="text-2xl text-red-500" />
        <h1 className="text-2xl font-bold text-gray-100 font-rubik">My Favorites</h1>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-700 mb-4" data-testid="favorites-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-rubik border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon className="text-lg" />
            {tab.label}
            {tab.count !== null && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-primary/20 text-primary' : 'bg-gray-700 text-gray-400'
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Sort Bar */}
      {!loading && ((activeTab === 'polls' && polls.length > 0) || (activeTab === 'representatives' && reps.length > 0)) && (
        <div className="flex items-center justify-end gap-2 mb-4" data-testid="sort-bar">
          <MdSort className="text-gray-400" />
          <select
            value={currentSort}
            onChange={e => setCurrentSort(e.target.value)}
            className="bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded-lg px-3 py-1.5 font-rubik focus:outline-none focus:border-primary cursor-pointer"
            data-testid="sort-select"
          >
            {currentSortOptions.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      ) : activeTab === 'tags' ? (
        <FavoriteTagsTab />
      ) : activeTab === 'polls' ? (
        polls.length === 0 ? (
          <EmptyState icon={MdPoll} message="No favorite polls yet" sub="Browse polls and tap the heart icon to save them here." />
        ) : (
          <div className="space-y-3">
            {sortedPolls.map(fav => (
              <div key={fav.item_id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4" data-testid={`fav-poll-${fav.item_id}`}>
                <Link to={`/polls/${fav.item_id}`} className="flex-1 min-w-0 flex items-center gap-3">
                  <p className="font-medium text-gray-900 font-rubik truncate">{fav.question}</p>
                  <PollStatusBadge status={fav.status} pollSource={fav.poll_source} />
                </Link>
                <button
                  onClick={() => removeFavorite('poll', fav.item_id, fav.question)}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                  data-testid={`remove-fav-poll-${fav.item_id}`}
                  title="Remove from favorites"
                >
                  <MdDelete className="text-lg" />
                </button>
              </div>
            ))}
          </div>
        )
      ) : (
        reps.length === 0 ? (
          <EmptyState icon={MdPeople} message="No favorite representatives yet" sub="Visit a representative's profile and tap the heart icon to save them here." />
        ) : (
          <div className="space-y-3">
            {sortedReps.map(fav => (
              <div key={fav.item_id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4" data-testid={`fav-rep-${fav.item_id}`}>
                <Link to={`/representatives/${fav.item_id}`} className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {fav.profile_picture_url ? (
                      <img src={fav.profile_picture_url} alt={fav.name} className="w-full h-full object-cover" />
                    ) : (
                      <MdPerson className="text-2xl text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 font-rubik truncate">{fav.name}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                      {fav.office && (
                        <span className="flex items-center gap-1">
                          <MdAccountBalance className="text-sm" />
                          {fav.office}
                          {fav.office_level && <span className="text-gray-400">({fav.office_level})</span>}
                        </span>
                      )}
                      {fav.state && (
                        <span className="flex items-center gap-1">
                          <MdLocationOn className="text-sm" />
                          {fav.state}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`text-center px-3 py-1.5 rounded-lg flex-shrink-0 ${
                    fav.match_percentage >= 75 ? 'bg-green-100 text-green-700' :
                    fav.match_percentage >= 50 ? 'bg-yellow-100 text-yellow-700' :
                    fav.match_percentage >= 25 ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`} data-testid={`match-pct-${fav.item_id}`}>
                    <div className="text-lg font-bold">{fav.match_percentage ?? 0}%</div>
                    <div className="text-[10px] font-medium uppercase tracking-wide">Match</div>
                  </div>
                </Link>
                <button
                  onClick={() => removeFavorite('representative', fav.item_id, fav.name)}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                  data-testid={`remove-fav-rep-${fav.item_id}`}
                  title="Remove from favorites"
                >
                  <MdDelete className="text-lg" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, message, sub }) {
  return (
    <div className="text-center py-16 bg-gray-800/30 rounded-xl border border-gray-700">
      <Icon className="text-5xl text-gray-500 mx-auto mb-3" />
      <h3 className="text-lg font-semibold text-gray-300 mb-1 font-rubik">{message}</h3>
      <p className="text-sm text-gray-500 font-rubik">{sub}</p>
    </div>
  );
}

function PollStatusBadge({ status, pollSource }) {
  if (!status || status === 'active') return null;
  const styles = {
    closed: 'bg-gray-100 text-gray-600 border-gray-200',
    expired: 'bg-amber-50 text-amber-700 border-amber-200',
    flagged: 'bg-red-50 text-red-600 border-red-200',
    pending: 'bg-blue-50 text-blue-600 border-blue-200',
  };
  const labels = {
    closed: 'Closed',
    expired: 'Expired',
    flagged: 'Flagged',
    pending: 'Pending',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${styles[status] || 'bg-gray-100 text-gray-500 border-gray-200'}`}
      data-testid={`poll-status-badge-${status}`}
    >
      {labels[status] || status}
    </span>
  );
}
